import {useEffect, useRef, useState} from 'react';
import {HEADING_SCROLL_OFFSET} from '../utils/headings';

// Track which heading the reader is currently on. Headings intersecting the
// upper reading band win; when none do (e.g. between long sections), the last
// heading scrolled above the band stays active.
export function useScrollSpy(ids: string[]): string | null {
  const [activeId, setActiveId] = useState<string | null>(null);
  const entriesRef = useRef(new Map<string, IntersectionObserverEntry>());
  const idsKey = ids.join('\n');

  useEffect(() => {
    // jsdom and very old browsers: no IntersectionObserver, no scroll-spy
    if (ids.length === 0 || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const entries = entriesRef.current;
    entries.clear();

    const observer = new IntersectionObserver(
      (changes) => {
        for (const entry of changes) {
          entries.set(entry.target.id, entry);
        }
        const visible = ids.filter((id) => entries.get(id)?.isIntersecting);
        if (visible.length > 0) {
          setActiveId(visible[0]);
          return;
        }
        const above = ids.filter((id) => {
          const entry = entries.get(id);
          return (
            entry !== undefined &&
            entry.boundingClientRect.top < HEADING_SCROLL_OFFSET
          );
        });
        if (above.length > 0) {
          setActiveId(above[above.length - 1]);
        }
      },
      {rootMargin: `-${HEADING_SCROLL_OFFSET}px 0px -55% 0px`},
    );

    for (const id of ids) {
      const element = document.getElementById(id);
      if (element) {
        observer.observe(element);
      }
    }

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  return activeId;
}
